import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { trackLesson } from "@/lib/learning/telemetry";
import { runWithSessionRetry } from "@/lib/auth/sessionGuard";

export interface SavedLessonProgress {
  current_stage: string;
  current_question_index: number;
  correct_count: number;
  answered_question_ids: string[];
}

export function useLessonProgress(lessonId: string | undefined) {
  const { user } = useAuth();
  const [savedProgress, setSavedProgress] = useState<SavedLessonProgress | null>(null);
  const [loaded, setLoaded] = useState(false);

  const loadProgress = useCallback(async () => {
    if (!user || !lessonId) { setLoaded(true); return null; }
    const { data } = await supabase
      .from("lesson_progress")
      .select("*")
      .eq("user_id", user.id)
      .eq("lesson_id", lessonId)
      .maybeSingle();
    
    if (data) {
      const progress: SavedLessonProgress = {
        current_stage: (data).current_stage,
        current_question_index: (data).current_question_index,
        correct_count: (data).correct_count,
        answered_question_ids: (data).answered_question_ids || [],
      };
      setSavedProgress(progress);
      setLoaded(true);
      return progress;
    }
    setLoaded(true);
    return null;
  }, [user, lessonId]);

  const saveProgress = useCallback(async (
    stage: string,
    questionIndex: number,
    correctCount: number,
    answeredIds: string[]
  ) => {
    if (!user || !lessonId) return;
    const payload = {
      user_id: user.id,
      lesson_id: lessonId,
      current_stage: stage,
      current_question_index: questionIndex,
      correct_count: correctCount,
      answered_question_ids: answeredIds,
      updated_at: new Date().toISOString(),
    };
    
    // Upsert
    const { data: existing } = await supabase
      .from("lesson_progress")
      .select("id")
      .eq("user_id", user.id)
      .eq("lesson_id", lessonId)
      .maybeSingle();

    if (existing) {
      await runWithSessionRetry(async () =>
        await supabase.from("lesson_progress").update(payload).eq("id", (existing).id)
      );
    } else {
      const { ok } = await runWithSessionRetry(async () =>
        await supabase.from("lesson_progress").insert(payload)
      );
      if (!ok) return;
      // First time we see this lesson for this user — log a lesson_started event.
      void trackLesson({
        eventType: "lesson_started",
        lessonId,
        sourceId: lessonId,
        attemptId: `${user.id}:${lessonId}:start`,
      });
    }
    if (stage === "completed") {
      void trackLesson({
        eventType: "lesson_completed",
        lessonId,
        sourceId: lessonId,
        attemptId: `${user.id}:${lessonId}:complete`,
        scoreDelta: correctCount,
      });
    }
  }, [user, lessonId]);

  const clearProgress = useCallback(async () => {
    if (!user || !lessonId) return;
    await supabase
      .from("lesson_progress")
      .delete()
      .eq("user_id", user.id)
      .eq("lesson_id", lessonId);
    setSavedProgress(null);
  }, [user, lessonId]);

  return { savedProgress, loaded, loadProgress, saveProgress, clearProgress };
}
